import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FormInput from '../../components/forms/FormInput';
import PasswordInput from '../../components/forms/PasswordInput';
import Button from '../../components/common/Button';
import { useToast } from '../../components/common/Toast';
import { authService } from '../../services/auth.service';
import { setCredentials } from '../../store/slices/authSlice';
import { useForm } from '../../hooks/useForm';
import { validators } from '../../utils/validators';
import { theme } from '../../config/theme';

const CustomerRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = React.useState(false);

  const { values, errors, touched, handleChange, handleBlur, validate } = useForm(
    {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      password: '',
      confirm_password: '',
    },
    {
      first_name: (value) => validators.name(value, 'First name'),
      last_name: (value) => validators.name(value, 'Last name'),
      email: (value) => validators.email(value),
      phone: (value) => validators.phone(value, false),
      password: (value) => validators.password(value),
      confirm_password: (value, allValues) => validators.confirmPassword(value, allValues.password),
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { confirm_password, ...registerData } = values;
      const response = await authService.registerCustomer(registerData);
      
      dispatch(setCredentials(response));
      toast.success('Registration successful! Welcome aboard!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const titleStyles = {
    fontSize: theme.typography.fontSize['2xl'],
    fontWeight: theme.typography.fontWeight.bold,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    color: theme.colors.text.primary,
  };

  const rowStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.md,
  };

  return (
    <form onSubmit={handleSubmit}>
      <h2 style={titleStyles}>Create Customer Account</h2>

      <div style={rowStyles}>
        <FormInput
          label="First Name"
          name="first_name"
          value={values.first_name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="John"
          error={errors.first_name}
          touched={touched.first_name}
          required
        />
        <FormInput
          label="Last Name"
          name="last_name"
          value={values.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Doe"
          error={errors.last_name}
          touched={touched.last_name}
          required
        />
      </div>

      <FormInput
        label="Email Address"
        name="email"
        type="email"
        value={values.email}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="john@example.com"
        error={errors.email}
        touched={touched.email}
        required
      />

      <FormInput
        label="Phone Number (Optional)"
        name="phone"
        type="tel"
        value={values.phone}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="9876543210"
        error={errors.phone}
        touched={touched.phone}
      />

      <PasswordInput
        label="Password"
        name="password"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Enter password"
        error={errors.password}
        touched={touched.password}
        required
      />

      <PasswordInput
        label="Confirm Password"
        name="confirm_password"
        value={values.confirm_password}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="Confirm password"
        error={errors.confirm_password}
        touched={touched.confirm_password}
        required
      />

      <Button type="submit" fullWidth loading={loading} disabled={loading}>
        Register as Customer
      </Button>
    </form>
  );
};

export default CustomerRegister;